import React, { useMemo } from 'react'
import { View } from 'react-native'
import { Feather } from '../../../components/icon/vectorIcons'
import styled from 'styled-components/native'

import CloudinaryImage from '../../../components/CloudinaryImage'
import { Flex } from '../../../components/FlexBox'
import { Text } from '../../../components/common/Text'
import useTranslation from '../../../hooks/useTranslation'
import { formatDate } from '../../../utils/format'
import {
  cloudinaryIdOf,
  durationMinutesOf,
  formatKeyOf,
  IContentFeedItem,
  subtitleOf,
  topicsOf,
  verbKeyOf
} from '../queries/getContentFeeds'
import Pill from './Pill'

/** Beyond this the pills crowd out the title on a narrow row. */
const MAX_PILLS = 3

export const THUMB_WIDTH = 96
export const THUMB_HEIGHT = 54

const Thumb = styled(View)`
  width: ${THUMB_WIDTH}px;
  height: ${THUMB_HEIGHT}px;
  flex-shrink: 0;
  flex-grow: 0;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${props => props.theme.colors.lightGrey};
`

/** Centred over the image, so it also labels the empty placeholder. */
const ThumbOverlay = styled(View)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  align-items: center;
  justify-content: center;
`

/**
 * A scrim behind the type icon. A plain white glyph disappears over pale or
 * busy artwork, and the thumbnails are arbitrary content stills -- this gives
 * the icon its own contrast rather than depending on what is underneath.
 */
const IconBadge = styled(View)`
  width: 28px;
  height: 28px;
  border-radius: 14px;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.55);
`

const ICONS: Record<string, 'play' | 'headphones' | 'file-text'> = {
  watch: 'play',
  listen: 'headphones',
  read: 'file-text'
}

/**
 * A boosted item only reaches members once it is published and its publish date
 * has passed -- the read path joins `feed` on `primarySortDate < NOW()`. Without
 * this the curator sees a filled row that renders to members as nothing.
 */
export const isNotYetVisible = (item: IContentFeedItem): boolean => {
  // Tested against `false` rather than falsy: `published` is non-null in the
  // schema, so an absent value means the field never arrived, and warning on
  // missing data puts a scary label on every row.
  if (item.published === false) return true
  if (!item.publishDate) return false
  return new Date(item.publishDate).getTime() > Date.now()
}

interface ContentItemSummaryProps {
  /** Null when the boost outlived a hard-deleted item. */
  item: IContentFeedItem | null
  /** Shown when there is no boost in this position at all. */
  emptyLabel?: string
}

export const ContentItemSummary = ({
  item,
  emptyLabel
}: ContentItemSummaryProps) => {
  const { t } = useTranslation()
  const cloudinaryId = item ? cloudinaryIdOf(item) : null
  const topics = item ? topicsOf(item) : []

  // One shape for all three types: "Watch · 48 min | Roundtable | Jul 22, 2026".
  // Any segment the type has no data for is dropped rather than left blank --
  // documents carry no page count, so they show format and date only.
  const metaLine = useMemo(() => {
    if (!item) return ''
    const minutes = durationMinutesOf(item)
    const formatKey = formatKeyOf(item)
    const lead = [
      t(`contentCuration:verb:${verbKeyOf(item)}`),
      minutes ? t('contentCuration:minutes', { count: minutes }) : null
    ]
      .filter(Boolean)
      .join(' · ')

    return [
      lead,
      formatKey ? t(formatKey) : null,
      subtitleOf(item),
      item.publishDate ? formatDate(item.publishDate) : null
    ]
      .filter(Boolean)
      .join('   |   ')
  }, [item, t])

  return (
    // Deliberately no flex={1}. The parent TableCol is a View, whose main axis
    // is the COLUMN -- flex: 1 there means "flex-basis: 0" on the HEIGHT, so
    // this box stops sizing to its content, collapses, and the fixed-height
    // thumbnail spills out of a too-short row into its neighbours. Width still
    // fills the column via the default align-items: stretch.
    <Flex
      flexDirection="row"
      alignItems="center"
      style={{ minHeight: THUMB_HEIGHT }}
    >
      <Thumb>
        {!!cloudinaryId && (
          <CloudinaryImage
            publicId={cloudinaryId}
            // height matters: c_fill with width alone does not constrain the
            // aspect ratio, so a portrait source comes back tall.
            transforms={{
              width: THUMB_WIDTH * 2,
              height: THUMB_HEIGHT * 2,
              crop: 'fill'
            }}
            style={{ width: THUMB_WIDTH, height: THUMB_HEIGHT }}
          />
        )}
        {!!item && (
          <ThumbOverlay>
            <IconBadge>
              <Feather name={ICONS[verbKeyOf(item)]} size={16} color="#FFF" />
            </IconBadge>
          </ThumbOverlay>
        )}
      </Thumb>
      <Flex flex={1} ml={3}>
        {!item && (
          <Text>
            {emptyLabel ?? t('contentCuration:detail:itemUnavailable')}
          </Text>
        )}
        {!!item && (
          <>
            <Text styles={{ fontSize: 14, fontWeight: '600' }}>
              {item.title}
            </Text>
            <Text styles={{ fontSize: 12 }}>{metaLine}</Text>
            {/* Pills get their own line rather than trailing the meta text. The
                text column is only ~260px wide in the picker, where sharing a
                line makes both wrap unpredictably -- and the row already
                reserves the thumbnail's height, so a third line is close to
                free. */}
            {!!topics.length && (
              <Flex
                flexDirection="row"
                alignItems="center"
                flexWrap="wrap"
                mt={1}
              >
                {topics.slice(0, MAX_PILLS).map(topic => (
                  <Pill key={topic.id}>{topic.name}</Pill>
                ))}
                {topics.length > MAX_PILLS && (
                  <Text styles={{ fontSize: 11 }}>
                    {`+${topics.length - MAX_PILLS}`}
                  </Text>
                )}
              </Flex>
            )}
            {isNotYetVisible(item) && (
              <Text styles={{ fontSize: 12, fontStyle: 'italic' }}>
                {t('contentCuration:hero:notVisible')}
              </Text>
            )}
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default ContentItemSummary
